// Reusable ArcaOS entity panels — every module composes these so each page answers
// directive #3: What / Why / Connected / What changed / What next.
import { useState, useEffect, useCallback } from "react";
import { osFetch } from "../api";
import { History, Link2, Activity as ActIcon, Sparkles, Lightbulb, Loader2, AlertTriangle, RotateCcw, Radar, ScanLine, CheckCircle2, Clock, XCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export function Card({ icon: Icon, title, children, testid, action }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={15} className="text-[#00F0FF]" />}
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{title}</span>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {children}
    </div>
  );
}

export function AIPanel({ entityType, entityId, base, ai, onUpdate }) {
  const [busy, setBusy] = useState("");
  const run = async (kind) => {
    setBusy(kind);
    try {
      const r = await osFetch(`${base}/${entityId}/${kind}`, { method: "POST" });
      onUpdate(r.ai);
      toast.success(`AI ${kind} ready`);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-4">
      <Card icon={Sparkles} title="What / Why (AI summary)" testid="os-ai-summary"
        action={<button onClick={() => run("summarize")} disabled={busy} className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline" data-testid="os-summarize-btn">{busy === "summarize" ? "…" : "Generate"}</button>}>
        {ai?.summary ? (
          <div className="text-sm text-slate-300 space-y-1">
            <p>{ai.summary}</p>
            {ai.what && <p className="text-slate-500"><span className="text-slate-600">What:</span> {ai.what}</p>}
            {ai.why && <p className="text-slate-500"><span className="text-slate-600">Why:</span> {ai.why}</p>}
          </div>
        ) : <p className="text-slate-600 text-sm">No summary yet — generate one.</p>}
      </Card>

      <Card icon={Lightbulb} title="What next (AI recommendations)" testid="os-ai-recs"
        action={<button onClick={() => run("recommend")} disabled={busy} className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline" data-testid="os-recommend-btn">{busy === "recommend" ? "…" : "Generate"}</button>}>
        {ai?.risk_flag ? <div className="mb-3 flex items-start gap-2 text-amber-300 text-sm"><AlertTriangle size={14} className="mt-0.5 shrink-0" />{ai.risk_flag}</div> : null}
        {ai?.recommendations?.length ? (
          <ul className="space-y-2">
            {ai.recommendations.map((r, i) => (
              <li key={i} className="text-sm">
                <span className={`font-mono text-[9px] uppercase mr-2 ${r.priority === "high" ? "text-rose-400" : r.priority === "medium" ? "text-amber-400" : "text-slate-500"}`}>{r.priority}</span>
                <span className="text-white">{r.action}</span>
                {r.rationale && <span className="text-slate-500 block ml-1 text-xs mt-0.5">{r.rationale}</span>}
              </li>
            ))}
          </ul>
        ) : <p className="text-slate-600 text-sm">No recommendations yet — generate them.</p>}
      </Card>
    </div>
  );
}

export function ConnectedPanel({ backlinks = [], links = [] }) {
  const all = [...links.map((l) => ({ ...l, dir: "→", other: `${l.to_type}:${l.to_id}` })),
               ...backlinks.map((l) => ({ ...l, dir: "←", other: `${l.from_type}:${l.from_id}` }))];
  return (
    <Card icon={Link2} title="Connected to" testid="os-connected">
      {all.length === 0 ? <p className="text-slate-600 text-sm">No connections yet.</p> : (
        <ul className="space-y-1.5">
          {all.map((l, i) => (
            <li key={i} className="text-sm text-slate-300 flex items-center gap-2">
              <span className="text-[#00F0FF] font-mono">{l.dir}</span>
              <span className="text-slate-500 font-mono text-xs">{l.relation}</span>
              <span className="text-slate-400 truncate">{l.other}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function ChangedPanel({ activity = [], history = [], base, entityId, onRestored }) {
  const [busy, setBusy] = useState(null);
  const restore = async (v) => {
    setBusy(v);
    try { await osFetch(`${base}/${entityId}/restore/${v}`, { method: "POST" }); toast.success(`Restored v${v}`); onRestored?.(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  return (
    <div className="space-y-4">
      <Card icon={History} title={`Version history (${history.length})`} testid="os-history">
        <ul className="space-y-1.5">
          {history.map((h) => (
            <li key={h.version} className="text-sm flex items-center gap-2">
              <span className="font-mono text-xs text-slate-500 w-8">v{h.version}</span>
              <span className="text-slate-400 truncate flex-1">{h.reason}</span>
              {h.version !== history[0]?.version && (
                <button onClick={() => restore(h.version)} disabled={busy === h.version} className="text-slate-500 hover:text-white" title={`Restore v${h.version}`} data-testid={`os-restore-${h.version}`}>
                  {busy === h.version ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                </button>
              )}
            </li>
          ))}
        </ul>
      </Card>
      <Card icon={ActIcon} title="What changed (activity)" testid="os-activity-panel">
        {activity.length === 0 ? <p className="text-slate-600 text-sm">No activity yet.</p> : (
          <ul className="space-y-1.5">
            {activity.map((a) => (
              <li key={a.id} className="text-xs text-slate-400 flex gap-2">
                <span className="text-slate-600 shrink-0">{new Date(a.created_at).toLocaleDateString()}</span>
                <span>{a.summary || a.verb}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// Category → chip classes (kept in sync with the Attention Center).
export const ATTN_CAT = {
  Critical: "text-rose-300 border-rose-500/40 bg-rose-500/10", High: "text-orange-300 border-orange-500/40 bg-orange-500/10",
  Medium: "text-amber-300 border-amber-500/40 bg-amber-500/10", Low: "text-slate-300 border-white/15 bg-white/5",
  Opportunity: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", Recommendation: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10",
  Warning: "text-amber-300 border-amber-500/40 bg-amber-500/10", Risk: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  Blocked: "text-rose-300 border-rose-500/40 bg-rose-500/10", "Waiting on Human": "text-violet-300 border-violet-500/40 bg-violet-500/10",
  "Waiting on AI": "text-blue-300 border-blue-500/40 bg-blue-500/10", "Needs Review": "text-slate-300 border-white/15 bg-white/5",
};

// A compact snooze menu supporting conditional snoozes.
export function SnoozeMenu({ onSnooze }) {
  const [open, setOpen] = useState(false);
  const opts = [
    { label: "24 hours", body: { hours: 24 } },
    { label: "3 days", body: { hours: 72 } },
    { label: "1 week", body: { hours: 168 } },
    { label: "Until manually resumed", body: { condition: { type: "until_manual" } } },
  ];
  return (
    <div className="relative inline-block">
      <button onClick={() => setOpen((o) => !o)} className="text-[11px] font-medium text-violet-300 hover:text-violet-200 flex items-center gap-1" data-testid="os-snooze-menu-trigger"><Clock size={13} /> Snooze <ChevronDown size={11} /></button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 mt-1 w-52 rounded-xl border border-white/10 bg-[#0a0a0a] p-1.5 z-20" data-testid="os-snooze-menu">
            {opts.map((o) => (
              <button key={o.label} onClick={() => { onSnooze(o.body); setOpen(false); }} className="w-full text-left px-3 py-1.5 rounded-lg text-xs text-slate-300 hover:bg-white/5" data-testid={`os-snooze-opt-${o.label.replace(/\s+/g, '-').toLowerCase()}`}>{o.label}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Attention for a single entity (used on every detail page) + Scan-now.
export function EntityAttentionPanel({ entityType, entityId }) {
  const [items, setItems] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => osFetch(`/attention?entity_type=${entityType}&entity_id=${entityId}&status=open`).then(setItems).catch(() => setItems([])), [entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  const scan = async () => {
    setScanning(true);
    try { const r = await osFetch(`/attention/scan/entity/${entityType}/${entityId}`, { method: "POST" }); toast.success(`Scan ${r.status} — ${r.items_generated} signals`); load(); }
    catch (e) { toast.error(e.message); } finally { setScanning(false); }
  };
  const act = async (id, kind, body) => {
    setBusy(id);
    try { await osFetch(`/attention/${id}/${kind}`, { method: "POST", body }); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  return (
    <Card icon={Radar} title="AI Attention" testid="os-entity-attention"
      action={<button onClick={scan} disabled={scanning} className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline flex items-center gap-1" data-testid="os-entity-scan-btn">{scanning ? <Loader2 size={11} className="animate-spin" /> : <ScanLine size={11} />} Scan now</button>}>
      {items === null ? <p className="text-slate-600 text-sm">Loading…</p>
        : items.length === 0 ? <p className="text-slate-600 text-sm">Nothing needs attention here.</p> : (
        <ul className="space-y-2.5">
          {items.map((it) => (
            <li key={it.id} className="rounded-xl border border-white/10 bg-black/30 p-3" data-testid={`os-entity-attn-${it.id}`}>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 ${ATTN_CAT[it.category] || ATTN_CAT["Needs Review"]}`}>{it.category}</span>
                <span className="text-[10px] font-mono text-slate-600">sev {it.severity}</span>
              </div>
              <p className="text-sm text-slate-300 mt-1.5">{it.reason}</p>
              {it.recommended_action && <p className="text-xs text-slate-500 mt-1">{it.recommended_action}</p>}
              <div className="flex items-center gap-3 mt-2">
                <button onClick={() => act(it.id, "resolve")} disabled={busy === it.id} className="text-[11px] text-emerald-300 hover:text-emerald-200 flex items-center gap-1"><CheckCircle2 size={12} /> Resolve</button>
                <SnoozeMenu onSnooze={(body) => act(it.id, "snooze", body)} />
                <button onClick={() => act(it.id, "dismiss")} disabled={busy === it.id} className="text-[11px] text-slate-400 hover:text-slate-200 flex items-center gap-1"><XCircle size={12} /> Dismiss</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
